import { useState, type ReactNode } from "react";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/frontend/components/ui/input";
import { cn } from "@/frontend/lib/utils";
import { InfoHint } from "@/frontend/components/ui/info-hint";
import { useFhirSourcesSearchQuery } from "@/frontend/queries/dashboard/patients";
import { useWorkspacePersona } from "../individual/useWorkspacePersona";
import { healthSystemSearchCopy } from "@/frontend/utils/dashboard/connections";
import { HealthSystemOptionRow } from "./HealthSystemSearch";
import {
  SEARCH_MIN_LENGTH,
  type HealthSystemOption,
  type HealthSystemRowAction,
  type HealthSystemSection,
} from "@/frontend/types/dashboard/health-systems";

type HealthSystemPickerProps = {
  /** Card heading. */
  title: string;
  /** Optional tooltip beside the heading. */
  hint?: string;
  /** Right-hand side of the header row, usually a count. */
  status?: ReactNode;
  /** Groups shown while nothing is being searched, in order. */
  sections: readonly HealthSystemSection[];
  /** Systems already picked, so their rows read as settled. */
  addedUrls: Set<string>;
  onSelect: (option: HealthSystemOption) => void;
  /** What picking a row means. Defaults to `add`. */
  action?: HealthSystemRowAction;
  /** Overrides the environment-derived placeholder. */
  placeholder?: string;
  /** True while `sections` are still being fetched. */
  isLoading?: boolean;
  /** Shown in the list when there is nothing to search and no group has rows. */
  emptyLabel?: string;
};

/**
 * One panel holding the search field and everything worth picking without
 * typing. Under two characters it shows labelled groups, and from two on the
 * whole list becomes search results. The list holds a fixed height while typing
 * so a shrinking result set cannot resize the panel.
 */
export function HealthSystemPicker({
  title,
  hint,
  status,
  sections,
  addedUrls,
  onSelect,
  action = "add",
  placeholder,
  isLoading = false,
  emptyLabel = "Search for a hospital, clinic or insurer above.",
}: HealthSystemPickerProps) {
  const [query, setQuery] = useState("");
  // The server scopes results to the workspace's environment, so the copy has to
  // say which one the reader is searching.
  const { environment } = useWorkspacePersona();
  const searchCopy = healthSystemSearchCopy(environment);
  const trimmed = query.trim();
  const isSearching = trimmed.length >= SEARCH_MIN_LENGTH;
  const { data: results, isLoading: resultsLoading } = useFhirSourcesSearchQuery(query);

  const filled = sections.filter((section) => section.sources.length > 0);

  /** Hands the pick up, then clears the query so the groups come back. */
  const pick = (option: HealthSystemOption) => {
    onSelect(option);
    setQuery("");
  };

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card">
      <div className="flex h-10 shrink-0 items-center justify-between gap-3 border-b border-border/60 px-4">
        <span className="flex min-w-0 items-center gap-1.5 text-meta font-semibold uppercase tracking-wide text-foreground">
          {title}
          {hint ? <InfoHint label={hint} /> : null}
        </span>
        {status ? <span className="shrink-0 text-caption text-muted-foreground">{status}</span> : null}
      </div>

      <div className="shrink-0 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setQuery("");
            }}
            placeholder={placeholder ?? searchCopy.placeholder}
            className="h-9 pl-9 text-body"
          />
        </div>
      </div>

      {/* Fixed height the moment anything is typed, so a result set that shrinks
          keystroke by keystroke cannot resize the panel under the person typing.
          Untouched, the list takes its natural height instead — pinning it at 320px
          there left dead space below a short group once a system was connected and
          dropped out of it. */}
      <div
        className={cn(
          "shrink-0 overflow-y-auto border-t border-border/60",
          query.length > 0 ? "h-80" : "max-h-80",
        )}
      >
        {isSearching ? (
          resultsLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : (results ?? []).length === 0 ? (
            <p className="flex h-full items-center justify-center px-4 text-center text-body text-muted-foreground">
              {searchCopy.emptyFor(trimmed)}
            </p>
          ) : (
            <div className="divide-y">
              {(results ?? []).map((source) => (
                <HealthSystemOptionRow
                  key={source.fhirBaseUrl}
                  source={source}
                  action={action}
                  added={addedUrls.has(source.fhirBaseUrl)}
                  onSelect={() => pick(source)}
                />
              ))}
            </div>
          )
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : filled.length === 0 ? (
          <p className="flex h-full items-center justify-center px-4 text-center text-body text-muted-foreground">
            {emptyLabel}
          </p>
        ) : (
          filled.map((section) => (
            <div key={section.id}>
              <p
                id={section.id}
                className="bg-muted/40 px-4 py-1.5 text-meta font-semibold uppercase tracking-wide text-foreground"
              >
                {section.label}
              </p>
              <div className="divide-y">
                {section.sources.map((source) => (
                  <HealthSystemOptionRow
                    key={source.fhirBaseUrl}
                    source={source}
                    action={action}
                    added={addedUrls.has(source.fhirBaseUrl)}
                    onSelect={() => pick(source)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
